import React from 'react';
import styled from 'styled-components';
import { Heading, Flex, Text, Button } from '@thenexlabs/uikit';
import { useTranslation } from 'contexts/Localization';
import BuyXpAction from 'components/Xp/BuyXpAction';
import Container from 'components/Layout/Container';
import { PageMeta } from 'components/Layout/Page'


const PageWrapper = styled.main`
  padding-top: 48px;
`;

const Section = styled.section`
  padding: 80px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor};

  &:last-of-type {
    border-bottom: none;
  }
`;

const HeroSection = styled(Section)`
  text-align: center;
  padding-top: 48px;
  padding-bottom: 48px;
  // border-top: 1px solid ${({ theme }) => theme.colors.borderColor};
`;

const StoreSection = styled(Section)`
  background: ${({ theme }) => theme.colors.background};
`;

const ValueGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
`;

const ValueCard = styled.div`
  background: ${({ theme }) => theme.colors.backgroundAlt};
  padding: 24px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-align: left;
`;

const StyledVideo = styled.video`
  object-fit: cover;
  width: 100%;
  height: 100%;
  max-height: 500px;
  border-radius: 16px;
  border: 1px solid lime;
  box-shadow: 0px 0px 20px lime;
`;

const FaqContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FaqItem = styled.details`
  background: ${({ theme }) => theme.colors.backgroundAlt};
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  cursor: pointer;
  text-align: left;

  & > summary {
    font-weight: bold;
    font-size: 1.2rem;
  }

  & > p {
    margin-top: 12px;
    line-height: 1.6;
  }
`;

const CtaButton = styled(Button)`
  padding: 5px;
  border-radius: 5px;
  box-shadow: 0 0 25px rgba(0, 255, 157, 0.5);
  &:hover {
    box-shadow: 0 0 40px rgba(0, 255, 157, 0.8);
  }
`;

const XpPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <PageMeta />
      <PageWrapper>
        {/* SECTION 1: THE HOOK */}
        <HeroSection>
          <Container>
            <Heading as="h1" scale="xxl" color="secondary" mb="16px">
              {t('XP: The Fuel for Your Metaverse Domination')}
            </Heading>
            <Text fontSize="20px" color="textSubtle" maxWidth="800px" margin="0 auto 32px">
              {t('XP is the official utility token of The NEX. It’s your key to unlocking exclusive assets, powerful upgrades, and a deeper connection to the entire ecosystem.')}
            </Text>
            <CtaButton as="a" href="#xp-store" scale="lg">
              {t('Get XP Now')}
            </CtaButton>
          </Container>
        </HeroSection>

        {/* SECTION 2: THE STORE (MAIN EVENT) */}
        <StoreSection id="xp-store">
          <Container>
            <Flex justifyContent="center">
              <BuyXpAction />
            </Flex>
          </Container>
        </StoreSection>

        {/* SECTION 3: THE "WHY" (VALUE PROPOSITION) */}
        <Section>
          <Container>
            <Heading as="h2" scale="xl" textAlign="center" mb="48px">{t('Unlock Your Potential with XP')}</Heading>
            <Flex flexDirection={['column', null, null, 'row']} alignItems="center" gap="48px">
              <Flex flex="1" justifyContent="center" alignItems="center">
                <StyledVideo src={'./videos/xp.mp4'} autoPlay loop muted playsInline />
              </Flex>
              <Flex flex="1" flexDirection="column" justifyContent="center">
                <ValueGrid>
                  <ValueCard>
                    <Heading as="h3" scale="lg" mb="8px">{t('Mint Exclusive NFTs')}</Heading>
                    <Text>{t('Use XP to mint limited-edition assets like the legendary GLITCH Boss Monsters and other future collections.')}</Text>
                  </ValueCard>
                  <ValueCard>
                    <Heading as="h3" scale="lg" mb="8px">{t('Unlock & Upgrade')}</Heading>
                    <Text>{t('Spend XP to access special in-game areas, level up your gear, and accelerate your character progression.')}</Text>
                  </ValueCard>
                  <ValueCard>
                    <Heading as="h3" scale="lg" mb="8px">{t('Marketplace Currency')}</Heading>
                    <Text>{t('XP is the primary currency for trading NFTs and other digital goods on The NEX marketplace.')}</Text>
                  </ValueCard>
                  <ValueCard>
                    <Heading as="h3" scale="lg" mb="8px">{t('Shape the Future')}</Heading>
                    <Text>{t('Hold XP to participate in governance votes that influence the future development of The NEX.')}</Text>
                  </ValueCard>
                </ValueGrid>
              </Flex>
            </Flex>
          </Container>
        </Section>

        {/* SECTION 4: THE TRUST BUILDER (FAQ) */}
        <Section>
          <Container>
            <Heading as="h2" scale="xl" textAlign="center" mb="32px">{t('Frequently Asked Questions')}</Heading>
            <FaqContainer>
              <FaqItem>
                <summary>{t('Is XP a cryptocurrency?')}</summary>
                <p>{t('Yes, XP is a utility token built on the blockchain. This ensures true ownership, transparency, and security for all your in-game assets and transactions.')}</p>
              </FaqItem>
              <FaqItem>
                <summary>{t('Can the value of XP change?')}</summary>
                <p>{t('Like other digital assets, the value of XP is determined by the market, based on supply and demand within The NEX ecosystem. Its primary purpose is for utility within our games and products.')}</p>
              </FaqItem>
              <FaqItem>
                <summary>{t('Where can I use my XP?')}</summary>
                <p>{t('You can use XP across the entire NEX ecosystem, including minting NFTs, buying/selling on the marketplace, upgrading items, and participating in special events.')}</p>
              </FaqItem>
            </FaqContainer>
          </Container>
        </Section>
      </PageWrapper>
    </>
  );
};

export default XpPage;